import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  withRouter,
  Link,
} from 'react-router-dom';
import {
  withStyles,
  withMobileDialog,
  Drawer,
  List,
  ListSubheader,
  Avatar,
  Typography,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { UserService } from '@pingum/api-services';
import { getUserNotifications } from '@pingum/app-state-management/userNotification/actions';
import { getUserSubscription } from '@pingum/app-state-management/userSubscription/actions';
import { toggleDrawer } from '@pingum/app-state-management/app/actions';

import { getTenantFromUrl } from '../../../utils/urlHelper';
import PingumLogo from '../../widgets/PingumLogo';
import FeedbackButton from '../../widgets/FeedbackButton';
import NavItem from './NavItem';
import {
  isUserAdmin,
  isUserOwner,
  isUserDeveloper,
} from '../../../utils/tenantAuthenticationHelper';
import { daysRemaining } from '../../../utils/dateHelper';
import styles from '../../../utils/styles';
import NavSection from './NavSection';

const getSrc = (imgKey, avatar) => {
  if (avatar && avatar.indexOf('http') >= 0) {
    // to avoid browser cache append random param
    return `${avatar}?date=${new Date(!imgKey
      ? 0
      : imgKey).toISOString()}`;
  }
  return avatar;
};

export class DrawerComponent extends React.Component {
  componentDidUpdate({ user: prevUser = {} }) {
    if (this.props.tenant && this.props.user && this.props.user !== prevUser) {
      this.props.getUserNotifications(this.props.user.user_id);
      this.props.getUserSubscription(this.props.user.user_id, this.props.user.email);
    }
  }

  render() {
    const {
      classes,
      user,
      tenant,
      tenantObject,
      fullScreen,
      open,
    } = this.props;
    const roles = UserService.getUserRolesForTenant(
      user,
      tenant,
    );
    const isOwner = isUserOwner(roles);
    const isAdmin = isUserAdmin(roles);
    const isDeveloper = isUserDeveloper(roles);

    let drawerClass = classes.drawerContainer;
    if (fullScreen) {
      drawerClass = classes.drawerContainerFullScreen;
    }
    if (!open && !fullScreen) {
      drawerClass = classes.drawerContainerClose;
    }

    return (
      <div className={drawerClass}>
        <Drawer
          variant={
            fullScreen
              ? 'temporary'
              : 'permanent'
          }
          anchor="left"
          open={this.props.open}
          classes={{
            paper: classNames(
              classes.drawerPaper,
              (!this.props.open && !fullScreen && classes.drawerPaperClose),
              (!this.props.open && fullScreen && classes.drawerPaperFullScreen),
            ),
          }}
          onClose={this.props.toggleDrawer}
          ModalProps={{ keepMounted: true }}
        >
          <div className={classes.drawer}>
            <Link to="/" href="/">
              <PingumLogo tenant={tenantObject} />
            </Link>
            {
              (
                !!isAdmin
                  && tenantObject.freeTrialEndDate
                  && new Date(tenantObject.freeTrialEndDate).getTime() > new Date().getTime()
              )
                  && (
                    <div className={classes.trialDaysChip}>
                      <Tooltip title="In order to continue using Pingum after the trial ends, you need to purchase a subscription from the billing page." placement="right">
                        <Chip
                          size="small"
                          color="secondary"
                          label={!open
                            ? 'Trial'
                            : `Trial ends in ${daysRemaining(tenantObject.freeTrialEndDate)} days`}
                          clickable
                          onClick={() => { window.location.href = '/settings/billing'; }}
                        />
                      </Tooltip>
                    </div>
                  )
            }
            <List className={classes.scroll}>
              {
                !!tenant
                  && <NavItem route="/home" label="Home" icon="home" />
              }
              {
                !!tenant
                  && <NavItem route="/dashboards" label="Dashboards" icon="dashboard" />
              }
              <NavSection label="Settings" icon="settings" drawerOpen={open}>
                <ListSubheader
                  disableGutters
                  disableSticky
                  classes={{
                    root: classNames(
                      classes.listSubheader,
                      !this.props.open && classes.listSubheaderClosed,
                    ),
                  }}
                >
                  Personal
                </ListSubheader>
                <NavItem route="/my-account" label="Account" />
                {
                  !!tenant
                    && [
                      <NavItem route="/my-subscriptions" label="Subscriptions" key="subs-nav-item" />,
                      <NavItem route="/settings/notifications" label="Notifications" key="notifications-nav-item" />,
                      <NavItem route="/settings/my-dashboards" label="Dashboards" key="dashboards-nav-item" />,
                    ]
                }
                <ListSubheader
                  disableGutters
                  disableSticky
                  classes={{
                    root: classNames(
                      classes.listSubheader,
                      !this.props.open && classes.listSubheaderClosed,
                    ),
                  }}
                >
                  Shared
                </ListSubheader>
                {
                  !!tenant
                    && !!isAdmin
                    && [
                      <NavItem route="/settings/workflows" label="Workflows" key="workflows-nav-item" />,
                      <NavItem route="/settings/events" label="Events" key="events-nav-item" />,
                      <NavItem route="/settings/forms" label="Forms" key="forms-nav-item" />,
                      <NavItem route="/settings/organization" label="Organization" key="orgs-nav-item" />,
                      <NavItem route="/settings/subscriptions" label="Subscriptions" key="subs-nav-item" />,
                      isOwner
                        ? <NavItem route="/settings/billing" label="Billing" key="billing-nav-item" />
                        : <span />,
                    ]
                }
                {
                  !!tenant
                    && !!isDeveloper
                    && tenantObject.stripeSubscription !== null
                    && [
                      <ListSubheader
                        disableGutters
                        disableSticky
                        key="integrations-nav-item"
                        classes={{
                          root: classNames(
                            classes.listSubheader,
                            !this.props.open && classes.listSubheaderClosed,
                          ),
                        }}
                      >
                        Integrations
                      </ListSubheader>,
                      <NavItem route="/developers" label="Developers" key="developer-nav-item" />,
                      /* <NavItem route="/sensors" label="Sensors" /> */
                    ]
                }
              </NavSection>
            </List>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0px',
              }}
            >
              <div style={{ paddingLeft: 6 }}>
                <Avatar
                  style={{ color: tenantObject.iconColor }}
                  src={getSrc((!tenantObject.updatedDate
                    ? tenantObject.creationDate
                    : tenantObject.updatedDate), tenantObject.avatar)}
                >
                  {
                    tenantObject
                      && tenantObject.displayName
                      && !tenantObject.avatar
                      && tenantObject.displayName.charAt(0)
                  }
                </Avatar>
              </div>
              <Typography
                style={{
                  color: '#777',
                  marginLeft: 16,
                }}
              >
                {tenantObject.displayName}
              </Typography>
            </div>
            <FeedbackButton />
          </div>
        </Drawer>
      </div>
    );
  }
}

DrawerComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  user: PropTypes.object,
  tenant: PropTypes.string,
  getUserNotifications: PropTypes.func,
  getUserSubscription: PropTypes.func,
  toggleDrawer: PropTypes.func,
  fullScreen: PropTypes.bool,
  tenantObject: PropTypes.object,
};

DrawerComponent.defaultProps = {
  open: true,
  user: { user_id: '' },
  tenant: undefined,
  getUserNotifications: () => {},
  getUserSubscription: () => {},
  toggleDrawer: () => {},
  fullScreen: false,
  tenantObject: {
    stripeSubscription: null,
    completedOnboarding: true,
    completedOnboardingSteps: [],
  },
};

export const mapStateToProps = (state) => ({
  user: state.user.user,
  isFetching: state.user.isFetchingUser,
  updateUserError: state.user.updateUserError,
  tenant: getTenantFromUrl(),
  open: state.app.drawerOpen,
  tenantObject: state.tenant.tenant,
});

export const mapDispatchToProps = (dispatch) => ({
  getUserNotifications: (auth0UserId) => dispatch(getUserNotifications(auth0UserId)),
  getUserSubscription: (auth0UserId, email) => dispatch(getUserSubscription(auth0UserId, email)),
  toggleDrawer: () => dispatch(toggleDrawer()),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(withStyles(styles, { withTheme: true })(DrawerComponent))));
